if (!customElements.get('bf-frame-configurator')) {
  customElements.define(
    'bf-frame-configurator',
    class BFFrameConfigurator extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';
        this.currency = this.dataset.currency || 'USD';
        const handleSelection = (event) => {
          if (event.target.name === 'properties[Wood Collection]') {
            this.updateSpecies();
          }
          this.update();
        };
        this.addEventListener('input', handleSelection);
        this.addEventListener('change', handleSelection);
        this.updateSpecies();
        this.update();
      }

      selectedValue(name) {
        return this.querySelector(`[name="${name}"]:checked`)?.value || '';
      }

      setConditional(key, visible, required = false) {
        const container = this.querySelector(`[data-bf-conditional="${key}"]`);
        if (!container) return;
        container.hidden = !visible;
        container.querySelectorAll('input, select, textarea').forEach((field) => {
          field.disabled = !visible;
          field.required = visible && required;
          if (
            !visible &&
            (field.tagName === 'TEXTAREA' || (field.tagName === 'INPUT' && ['text', 'search'].includes(field.type)))
          ) {
            field.value = '';
          }
        });
      }

      update() {
        const size = this.querySelector('[data-bf-size]')?.value || '18x24';
        const collection = this.selectedValue('properties[Wood Collection]') || 'Signature Collection';
        const isCurlyMaple = collection === 'Curly Maple Collection';
        const isMattedSize = size === '20x26';

        this.setConditional('species-standard', !isCurlyMaple, true);
        this.setConditional('species-curly', isCurlyMaple);
        this.setConditional('curly-options', isCurlyMaple);

        this.setConditional('mat-section', isMattedSize);
        if (!isMattedSize) {
          const noMat = this.querySelector('[name="properties[Mat Option]"][value="No Mat"]');
          if (noMat) noMat.checked = true;
        }

        const activeMat = isMattedSize
          ? this.querySelector('[name="properties[Mat Option]"]:checked')?.value || 'No Mat'
          : 'No Mat';
        this.setConditional('mat-top', ['Single Mat', 'Double Mat', 'Triple Mat'].includes(activeMat), true);
        this.setConditional('mat-middle', ['Double Mat', 'Triple Mat'].includes(activeMat), true);
        this.setConditional('mat-bottom', activeMat === 'Triple Mat', true);

        const curlyFinish = this.querySelector('[data-bf-curly-finish]')?.value;
        this.setConditional('curly-custom-stain', isCurlyMaple && curlyFinish === 'Custom Stain', true);
        this.setConditional('curly-primary-stain', isCurlyMaple && curlyFinish === 'Two-Tone Stain', true);
        this.setConditional('curly-secondary-stain', isCurlyMaple && curlyFinish === 'Two-Tone Stain', true);

        const truVuePrice = this.truVuePrices[size] || 0;
        const truVueLabel = this.querySelector('[data-bf-truvue-price]');
        if (truVueLabel) truVueLabel.textContent = `+${this.formatMoney(truVuePrice)}`;

        this.updatePrice();
      }

      updateSpecies() {
        const collection = this.selectedValue('properties[Wood Collection]') || 'Signature Collection';
        const speciesSelect = this.querySelector('[data-bf-species]');
        if (collection === 'Curly Maple Collection') return;
        const species = this.speciesByCollection[collection] || [];
        const previousValue = speciesSelect?.value;
        if (!speciesSelect) return;

        speciesSelect.replaceChildren(
          ...species.map((name) => {
            const option = document.createElement('option');
            option.value = name;
            option.textContent = name;
            return option;
          })
        );

        if (species.includes(previousValue)) speciesSelect.value = previousValue;
      }

      updatePrice() {
        const size = this.querySelector('[data-bf-size]')?.value || '18x24';
        const collection = this.selectedValue('properties[Wood Collection]') || 'Signature Collection';
        const frameStyle = this.selectedValue('properties[Real Lightning]') || 'Standard Frame';
        const acrylic = this.selectedValue('properties[Acrylic]');
        const selectedMat = this.querySelector('[name="properties[Mat Option]"]:checked');
        const selectedGlow = this.querySelector('[name="properties[Glow Enhancement]"]:checked');

        const signature = this.signaturePrices[size]?.[frameStyle] || 0;
        const collectionUpgrade = {
          'Signature Collection': 0,
          'Select Collection': 7500,
          'Reserve Collection': 15000,
          'Curly Maple Collection': 15000
        }[collection] || 0;
        const acrylicUpgrade =
          acrylic === 'TruVue Optium Museum Acrylic Upgrade' ? this.truVuePrices[size] || 0 : 0;
        const matUpgrade = size === '20x26' ? Number(selectedMat?.dataset.bfMatPrice || 0) : 0;
        const glowUpgrade =
          collection === 'Curly Maple Collection' ? Number(selectedGlow?.dataset.bfGlowPrice || 0) : 0;

        const total = signature + collectionUpgrade + acrylicUpgrade + matUpgrade + glowUpgrade;
        const totalText = this.formatMoney(total);
        const configuratorTotal = this.querySelector('[data-bf-estimated-total]');
        const totalProperty = this.querySelector('[data-bf-total-property]');

        if (configuratorTotal) configuratorTotal.textContent = totalText;
        if (totalProperty) totalProperty.value = totalText;
        this.updateProductPrice(totalText);
      }

      updateProductPrice(totalText) {
        const productInfo = this.closest('.product__info-container');
        const priceRoot = productInfo?.querySelector('[id^="price-"]');
        if (!priceRoot) return;

        priceRoot.querySelectorAll('.price-item--regular, .price-item--sale.price-item--last').forEach((price) => {
          price.textContent = totalText;
        });

        let note = priceRoot.querySelector('[data-bf-price-note]');
        if (!note) {
          note = document.createElement('small');
          note.dataset.bfPriceNote = '';
          note.className = 'bf-configurator__price-note';
          note.textContent = 'Estimated configured total';
          priceRoot.appendChild(note);
        }
      }

      formatMoney(cents) {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.currency
        }).format(cents / 100);
      }

      get signaturePrices() {
        return {
          '5x5': { 'Standard Frame': 10000, 'Real Lightning Upgrade': 15000 },
          '6x6': { 'Standard Frame': 11000, 'Real Lightning Upgrade': 16500 },
          '7x7': { 'Standard Frame': 12000, 'Real Lightning Upgrade': 18000 },
          '5x7': { 'Standard Frame': 12500, 'Real Lightning Upgrade': 18500 },
          '8x10': { 'Standard Frame': 15000, 'Real Lightning Upgrade': 22000 },
          '11x14': { 'Standard Frame': 20000, 'Real Lightning Upgrade': 28000 },
          '11x17': { 'Standard Frame': 22500, 'Real Lightning Upgrade': 31500 },
          '16x20': { 'Standard Frame': 27500, 'Real Lightning Upgrade': 37000 },
          '18x24': { 'Standard Frame': 30000, 'Real Lightning Upgrade': 40000 },
          '20x26': { 'Standard Frame': 35000, 'Real Lightning Upgrade': 46000 },
          '20.5x33': { 'Standard Frame': 47500, 'Real Lightning Upgrade': 60000 },
          '12x36': { 'Standard Frame': 52500, 'Real Lightning Upgrade': 65500 },
          '24x36': { 'Standard Frame': 57500, 'Real Lightning Upgrade': 72500 }
        };
      }

      get truVuePrices() {
        return {
          '5x5': 5000,
          '6x6': 5000,
          '7x7': 5000,
          '5x7': 5000,
          '8x10': 6000,
          '11x14': 7500,
          '11x17': 8500,
          '16x20': 9500,
          '18x24': 10000,
          '20x26': 12500,
          '20.5x33': 17500,
          '12x36': 17500,
          '24x36': 20000
        };
      }

      get speciesByCollection() {
        return {
          'Signature Collection': [
            'White Limba',
            'Padauk',
            'Purpleheart',
            'Walnut',
            'Cedar',
            'Birdseye Maple',
            'Hard Maple'
          ],
          'Select Collection': [
            'Leopardwood',
            'Black Limba',
            'Mahogany',
            'Canarywood',
            'Red Zebrawood',
            'Wenge'
          ],
          'Reserve Collection': [
            'Bloodwood',
            'Live Edge Walnut',
            'Curupay',
            'Black Palm',
            'Zebrawood',
            'Caribbean Rosewood',
            'Bocote',
            'Spalted Tamarind'
          ]
        };
      }
    }
  );
}
